import { create } from 'zustand'
import { logout as apiLogout, selectCompany as apiSelectCompany } from '../api/client'

export interface UserCompany {
  id: string
  code: string
  name: string
  currency?: string
}

export interface UserState {
  id: number | null
  email: string | null
  name: string | null
  status: string | null
  default_company_id: string | null
  current_company_id: string | null
  companies: UserCompany[]
  modules: string[]
  roles: string[]
  permissions: string[]
  loaded: boolean
  load: () => Promise<void>
  logout: () => Promise<void>
  setCompany: (companyId: string) => void
  selectCompany: (companyId: string) => Promise<void>
}

const initialState = {
  id: null,
  email: null,
  name: null,
  status: null,
  default_company_id: null,
  current_company_id: null,
  companies: [] as UserCompany[],
  modules: [] as string[],
  roles: [] as string[],
  permissions: [] as string[],
}

export const useUser = create<UserState>((set) => ({
  ...initialState,
  loaded: false,

  async load() {
    try {
      const r = await fetch('/api/auth/me', {
        credentials: 'include',
        headers: { Accept: 'application/json' },
      })
      if (!r.ok) {
        set({ ...initialState, loaded: true })
        return
      }
      const data = (await r.json()) as Omit<
        UserState,
        'loaded' | 'load' | 'logout' | 'setCompany' | 'selectCompany'
      >
      set({
        id: data.id,
        email: data.email,
        name: data.name,
        status: data.status,
        default_company_id: data.default_company_id,
        current_company_id: data.current_company_id,
        companies: data.companies,
        modules: data.modules,
        roles: data.roles,
        permissions: data.permissions,
        loaded: true,
      })
    } catch {
      set({ ...initialState, loaded: true })
    }
  },

  async logout() {
    try {
      await apiLogout()
    } finally {
      set({ ...initialState, loaded: true })
    }
  },

  setCompany(companyId: string) {
    set({ current_company_id: companyId })
  },

  async selectCompany(companyId: string) {
    await apiSelectCompany(companyId)
    set({ current_company_id: companyId })
  },
}))
